import { useRef, useState } from "react";
import { Link2, Upload, Video, Check, Loader2, ArrowLeft, Sparkles, PenLine, X, Film, FolderPlus } from "lucide-react";
const Youtube = Video;
import { toast } from "sonner";
import { api, errMsg, img } from "../../lib/api";
import { useCrud } from "../../lib/adminApi";
import { tr } from "../../lib/i18n";
import { uploadVideo, captureThumb, uploadImageBlob, MAX_VIDEO, VIDEO_TYPES, fmtDuration } from "../../lib/video";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";
import { Field, I18nInput, ImageUpload, SlugField, SeoFields } from "./Fields";
import { DateTimePicker } from "../ui/datetime-picker";

const PROVIDER = { youtube: "YouTube", vimeo: "Vimeo", dailymotion: "Dailymotion", upload: "Bilgisayardan" };
const detect = (u) => (/youtu\.?be/.test(u) ? "youtube" : /vimeo\.com/.test(u) ? "vimeo" : /dailymotion\.com|dai\.ly/.test(u) ? "dailymotion" : null);

function Steps({ step, labels }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs mb-2" data-testid="wizard-steps">
      {labels.map((l, i) => (
        <div key={l} className="flex items-center gap-2">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold transition-colors ${step > i ? "bg-emerald-600 text-white" : step === i ? "bg-stone-900 text-white" : "bg-stone-200 text-stone-500"}`}>{step > i ? <Check size={12} /> : i + 1}</span>
          <span className={step === i ? "font-medium" : "text-stone-400"}>{l}</span>
          {i < labels.length - 1 && <span className="w-8 h-px bg-stone-300" />}
        </div>
      ))}
    </div>
  );
}

function Choice({ icon: Icon, title, sub, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="text-left rounded-xl border-2 border-stone-200 p-5 hover:border-stone-900 hover:-translate-y-0.5 transition-[border-color,transform] group">
      <Icon size={26} className="mb-3 text-stone-500 group-hover:text-stone-900 transition-colors" />
      <p className="font-semibold">{title}</p>
      <p className="text-xs text-stone-500 mt-1">{sub}</p>
    </button>
  );
}

function LinkStep({ onDone }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const prov = detect(url);
  const go = async (fetch) => {
    setBusy(true);
    try {
      const m = (await api.get("/admin/video-meta", { params: { url, fetch } })).data;
      if (fetch && !m.title) toast.message("Bilgiler alınamadı, lütfen kendiniz doldurun");
      onDone({ source: m.source, source_url: url, external_id: m.external_id, embed_url: m.embed_url, youtube_id: m.youtube_id,
        title: { tr: m.title }, description: { tr: m.description }, thumbnail: m.thumbnail, duration: m.duration, published_at: m.published_at || undefined });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-5">
      <Field label="Video bağlantısı" hint="YouTube, Vimeo veya Dailymotion bağlantısını yapıştırın">
        <Input autoFocus value={url} onChange={(e) => setUrl(e.target.value.trim())} placeholder="https://www.youtube.com/watch?v=…" data-testid="wizard-url-input" />
      </Field>
      {url && !prov && <p className="text-sm text-red-600" data-testid="wizard-url-error">Bu bağlantı desteklenmiyor.</p>}
      {prov && (
        <div className="rounded-xl border bg-stone-50 p-5 space-y-4" data-testid="wizard-fetch-question">
          <p className="text-sm"><span className="font-semibold">{PROVIDER[prov]}</span> videosu algılandı. Başlık, açıklama ve kapak görseli {PROVIDER[prov]}'dan alınsın mı?</p>
          <div className="flex flex-wrap gap-3">
            <Button type="button" disabled={busy} onClick={() => go(true)} data-testid="wizard-fetch-yes">{busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Sparkles size={15} className="mr-2" />}Evet, otomatik al</Button>
            <Button type="button" variant="outline" disabled={busy} onClick={() => go(false)} data-testid="wizard-fetch-no"><PenLine size={15} className="mr-2" />Hayır, kendim yazacağım</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadStep({ onDone }) {
  const [file, setFile] = useState(null);
  const [pct, setPct] = useState(0);
  const ctrl = useRef(null);
  const start = async (f) => {
    if (!VIDEO_TYPES.includes(f.type)) return toast.error("Yalnızca MP4, WebM veya MOV yükleyebilirsiniz");
    if (f.size > MAX_VIDEO) return toast.error("Video en fazla 1 GB olabilir");
    setFile(f);
    ctrl.current = new AbortController();
    try {
      const thumbP = captureThumb(f);
      const up = await uploadVideo(f, setPct, ctrl.current.signal);
      const { blob, duration } = await thumbP;
      const name = f.name.replace(/\.[^.]+$/, "");
      const thumbnail = blob ? await uploadImageBlob(blob, name) : "";
      onDone({ source: "upload", file_id: up.id, title: { tr: name.replace(/[-_]+/g, " ") }, description: { tr: "" }, thumbnail, duration });
    } catch (e) {
      if (!ctrl.current.signal.aborted) toast.error(errMsg(e));
      setFile(null); setPct(0);
    }
  };
  if (file) return (
    <div className="space-y-3 py-4" data-testid="wizard-upload-progress">
      <p className="text-sm font-medium truncate">{file.name}</p>
      <Progress value={pct * 100} />
      <div className="flex justify-between text-xs text-stone-500">
        <span>{Math.round(pct * 100)}% · {(file.size * pct / 1048576).toFixed(0)} / {(file.size / 1048576).toFixed(0)} MB</span>
        <button type="button" onClick={() => ctrl.current?.abort()} className="hover:text-red-600 flex items-center gap-1" data-testid="wizard-upload-cancel"><X size={12} />İptal</button>
      </div>
      <p className="text-xs text-stone-400">Yükleme sürerken bu pencereyi kapatmayın.</p>
    </div>
  );
  return (
    <label className="block rounded-xl border-2 border-dashed border-stone-300 p-10 text-center cursor-pointer hover:border-stone-900 transition-colors"
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); e.dataTransfer.files[0] && start(e.dataTransfer.files[0]); }} data-testid="wizard-dropzone">
      <Upload size={28} className="mx-auto mb-3 text-stone-400" />
      <p className="font-medium">Videoyu buraya sürükleyin veya seçmek için tıklayın</p>
      <p className="text-xs text-stone-500 mt-1">MP4, WebM, MOV · en fazla 1 GB · kapak görseli videodan otomatik alınır</p>
      <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => e.target.files[0] && start(e.target.files[0])} data-testid="wizard-file-input" />
    </label>
  );
}

function TypeStep({ albums, onPick }) {
  const [albumMode, setAlbumMode] = useState(false);
  const [sel, setSel] = useState(albums[0]?.id || "new");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { save } = useCrud("video_albums");
  const next = async () => {
    if (sel !== "new") return onPick(sel);
    if (!name.trim()) return toast.error("Albüm adını yazın");
    setBusy(true);
    const r = await save({ title: { tr: name.trim() }, description: { tr: "" }, status: "published", featured: false }, true);
    setBusy(false);
    if (r) { toast.success(`"${name.trim()}" albümü oluşturuldu`); onPick(r.id); }
  };
  if (!albumMode) return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Choice icon={Film} title="Tek video" sub="Albüme bağlı olmadan yayınlanır. Sonradan albüme taşıyabilirsiniz." onClick={() => onPick(null)} testid="wizard-type-single" />
      <Choice icon={FolderPlus} title="Albüme ekle" sub="Mevcut bir albüme veya yeni oluşturacağınız albüme eklenir." onClick={() => setAlbumMode(true)} testid="wizard-type-album" />
    </div>
  );
  return (
    <div className="space-y-4" data-testid="wizard-album-pick">
      <Field label="Hangi albüm?">
        <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="wizard-album-select">
          {albums.map((a) => <option key={a.id} value={a.id}>{tr(a.title)}</option>)}
          <option value="new">+ Yeni albüm oluştur</option>
        </select>
      </Field>
      {sel === "new" && <Field label="Yeni albüm adı"><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="ör. Konuşmadan Olmaz" data-testid="wizard-new-album-name" /></Field>}
      <div className="flex gap-3">
        <Button type="button" variant="ghost" onClick={() => setAlbumMode(false)} data-testid="wizard-album-cancel"><ArrowLeft size={14} className="mr-1" />Vazgeç</Button>
        <Button type="button" disabled={busy} onClick={next} data-testid="wizard-album-next">Devam</Button>
      </div>
    </div>
  );
}

function DetailsStep({ v, setV, albums, onSave, busy }) {
  const set = (k) => (x) => setV((p) => ({ ...p, [k]: x }));
  return (
    <div className="grid md:grid-cols-[minmax(0,1fr)_280px] gap-6">
      <div className="space-y-4 min-w-0">
        <I18nInput label="Başlık" value={v.title} onChange={set("title")} testid="wizard-title" maxLength={200} />
        <I18nInput label="Açıklama" value={v.description} onChange={set("description")} multiline rows={6} testid="wizard-description" maxLength={5000} />
        <SlugField value={v.slug} onChange={set("slug")} source={v.title?.tr} prefix="/videolar/izle/" testid="wizard-slug" />
        <SeoFields value={v.seo} onChange={set("seo")} title={v.title} body={v.description} path={`/videolar/izle/${v.slug || ""}`} />
      </div>
      <div className="space-y-4">
        <div className="rounded-lg border p-3 text-xs text-stone-600 space-y-1" data-testid="wizard-source-info">
          <p><span className="font-semibold">Kaynak:</span> {PROVIDER[v.source]}</p>
          {v.duration > 0 && <p><span className="font-semibold">Süre:</span> {fmtDuration(v.duration)}</p>}
        </div>
        <ImageUpload label="Kapak görseli" value={v.thumbnail} onChange={set("thumbnail")} testid="wizard-thumbnail" alt={v.title?.tr} />
        <Field label="Albüm">
          <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={v.album_id || ""} onChange={(e) => set("album_id")(e.target.value || null)} data-testid="wizard-album">
            <option value="">— Albümsüz (tek video) —</option>
            {albums.map((a) => <option key={a.id} value={a.id}>{tr(a.title)}</option>)}
          </select>
        </Field>
        <Field label="Yayın tarihi">
          <DateTimePicker value={v.published_at || ""} onChange={(iso) => set("published_at")(iso)} testId="wizard-date" />
        </Field>
        <Field label="Durum">
          <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={v.status || "published"} onChange={(e) => set("status")(e.target.value)} data-testid="wizard-status">
            <option value="published">Yayında</option><option value="draft">Taslak</option>
          </select>
        </Field>
        <Button className="w-full" disabled={busy || !v.title?.tr} onClick={onSave} data-testid="wizard-save">{busy ? "Kaydediliyor…" : "Videoyu kaydet"}</Button>
      </div>
    </div>
  );
}

export function VideoWizard({ open, onClose, albumId, albums, edit, count }) {
  const preset = albumId !== undefined;
  const flow = preset ? ["source", "media", "details"] : ["type", "source", "media", "details"];
  const [step, setStep] = useState(edit ? flow.length - 1 : 0);
  const [mode, setMode] = useState(null);
  const [target, setTarget] = useState(albumId ?? null);
  const [v, setV] = useState(edit || null);
  const [busy, setBusy] = useState(false);
  const { save } = useCrud("videos");
  const cur = flow[step];
  const got = (data) => { setV({ album_id: target, status: "published", seo: {}, order: count, ...data }); setStep(flow.length - 1); };
  const submit = async () => {
    setBusy(true);
    const r = await save({ ...v, url: v.source_url, slug: v.slug ?? "" });
    setBusy(false);
    if (r) onClose();
  };
  const back = () => { if (cur === "details") setV(null); setStep(step - 1); };
  const TITLES = { type: "Tek video mu, albüme mi?", source: "Videoyu nasıl eklemek istersiniz?", media: mode === "link" ? "Bağlantıyı yapıştırın" : "Videoyu yükleyin", details: "Video bilgileri ve SEO" };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="video-wizard">
        <DialogHeader>
          {!edit && <Steps step={step} labels={flow.map((k) => ({ type: "Tür", source: "Kaynak", media: "Video", details: "Bilgiler" })[k])} />}
          <DialogTitle>{edit ? "Videoyu düzenle" : TITLES[cur]}</DialogTitle>
          <DialogDescription>{cur === "details" ? "Başlık ve açıklama arama motorlarında ve paylaşımlarda görünür." : "Adım adım ilerleyin, istediğiniz zaman geri dönebilirsiniz."}</DialogDescription>
        </DialogHeader>
        {cur === "type" && <TypeStep albums={albums} onPick={(id) => { setTarget(id); setStep(step + 1); }} />}
        {cur === "source" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Choice icon={Link2} title="Bağlantı ile ekle" sub="YouTube, Vimeo veya Dailymotion" onClick={() => { setMode("link"); setStep(step + 1); }} testid="wizard-choice-link" />
            <Choice icon={Upload} title="Bilgisayardan yükle" sub="MP4, WebM, MOV · en fazla 1 GB" onClick={() => { setMode("upload"); setStep(step + 1); }} testid="wizard-choice-upload" />
          </div>
        )}
        {cur === "media" && (mode === "link" ? <LinkStep onDone={got} /> : <UploadStep onDone={got} />)}
        {cur === "details" && v && <DetailsStep v={v} setV={setV} albums={albums} onSave={submit} busy={busy} />}
        {step > 0 && !edit && <Button variant="ghost" size="sm" className="self-start" onClick={back} data-testid="wizard-back"><ArrowLeft size={14} className="mr-1" />Geri</Button>}
      </DialogContent>
    </Dialog>
  );
}

export const SourceIcon = ({ source }) => (source === "youtube" ? <Youtube size={14} className="text-red-600" /> : source === "upload" ? <Upload size={14} /> : <Link2 size={14} />);
export const thumbSrc = (u) => img(u);
export { PROVIDER };
